export type ChatProviderName = "crisp" | "intercom" | "pylon";
export type ChatTheme = "light" | "dark";
export type ChatStatus = "ok" | "disabled" | "unsupported" | "reload_required" | "error";

export type ChatEventValue =
  | string
  | number
  | boolean
  | null
  | ChatEventValue[]
  | { [key: string]: ChatEventValue };

export type ChatEventMetadata = Record<string, ChatEventValue>;

export interface ChatUser {
  id: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  accountExternalId?: string;
  createdAt?: string | number;
  /** A short-lived, server-generated signature or token for the selected provider. */
  verificationToken?: string;
}

export interface ChatCapabilities {
  eventTracking: boolean;
  identityHotSwap: boolean;
  launcherVisibility: boolean;
}

export interface ChatResult {
  status: ChatStatus;
  provider: ChatProviderName | null;
  operation: string;
  message?: string;
}

export interface ChatClient {
  readonly provider: ChatProviderName | null;
  readonly capabilities: ChatCapabilities;
  /** Calls made while loading settle only after their operation runs in FIFO order. */
  initialize(): Promise<ChatResult>;
  identifyUser(user: ChatUser): Promise<ChatResult>;
  logout(): Promise<ChatResult>;
  show(): Promise<ChatResult>;
  hide(): Promise<ChatResult>;
  open(): Promise<ChatResult>;
  close(): Promise<ChatResult>;
  trackEvent(name: string, metadata?: ChatEventMetadata): Promise<ChatResult>;
}

export interface DisabledChatConfig {
  enabled: false;
  /** Present only when runtime validation failed. Safe to expose to the browser. */
  reason?: string;
}

interface BaseEnabledChatConfig {
  enabled: true;
  appId: string;
}

export interface CrispChatConfig extends BaseEnabledChatConfig {
  provider: "crisp";
}

export interface IntercomChatConfig extends BaseEnabledChatConfig {
  provider: "intercom";
  intercomRegion: "us" | "eu" | "au";
  intercomApiBase: string;
}

export interface PylonChatConfig extends BaseEnabledChatConfig {
  provider: "pylon";
}

export type EnabledChatConfig = CrispChatConfig | IntercomChatConfig | PylonChatConfig;
export type PublicChatConfig = DisabledChatConfig | EnabledChatConfig;

export interface ChatScriptDescriptor {
  id: string;
  src: string;
}

export interface ChatAdapter {
  readonly provider: ChatProviderName;
  readonly capabilities: ChatCapabilities;
  readonly script: ChatScriptDescriptor;
  prepare(theme: ChatTheme): Promise<ChatResult>;
  onScriptReady(): Promise<ChatResult>;
  identifyUser(user: ChatUser): Promise<ChatResult>;
  logout(): Promise<ChatResult>;
  show(): Promise<ChatResult>;
  hide(): Promise<ChatResult>;
  open(): Promise<ChatResult>;
  close(): Promise<ChatResult>;
  trackEvent(name: string, metadata?: ChatEventMetadata): Promise<ChatResult>;
  setTheme(theme: ChatTheme): Promise<ChatResult>;
  updateRoute(pathname: string): Promise<ChatResult>;
}

export const DISABLED_CHAT_CAPABILITIES: ChatCapabilities = Object.freeze({
  eventTracking: false,
  identityHotSwap: false,
  launcherVisibility: false,
});

export const CHAT_PROVIDER_CAPABILITIES: Record<ChatProviderName, ChatCapabilities> = {
  crisp: Object.freeze({
    eventTracking: true,
    identityHotSwap: true,
    launcherVisibility: true,
  }),
  intercom: Object.freeze({
    eventTracking: true,
    identityHotSwap: true,
    launcherVisibility: true,
  }),
  pylon: Object.freeze({
    eventTracking: false,
    identityHotSwap: false,
    launcherVisibility: true,
  }),
};

const loggedErrors = new Set<string>();

export const createChatResult = (
  provider: ChatProviderName | null,
  operation: string,
  status: ChatStatus = "ok",
  message?: string,
): ChatResult => ({
  status,
  provider,
  operation,
  ...(message ? { message } : {}),
});

export const disabledChatResult = (operation: string): ChatResult =>
  createChatResult(null, operation, "disabled", "Chat is disabled.");

export const chatErrorResult = (
  provider: ChatProviderName | null,
  operation: string,
): ChatResult =>
  createChatResult(
    provider,
    operation,
    "error",
    `The ${provider ?? "chat"} ${operation} operation failed.`,
  );

/**
 * Shared exception boundary for both adapters and the manager. Diagnostics are
 * deliberately fixed strings so vendor errors cannot leak identity or payloads.
 */
export async function safeChatOperation(
  provider: ChatProviderName | null,
  operation: string,
  callback: () => ChatResult | void | Promise<ChatResult | void>,
): Promise<ChatResult> {
  try {
    const result = await callback();
    return result ?? createChatResult(provider, operation);
  } catch {
    const logKey = `${provider ?? "disabled"}:${operation}`;
    if (!loggedErrors.has(logKey)) {
      loggedErrors.add(logKey);
      console.error(`[chat:${provider ?? "disabled"}] ${operation} failed.`);
    }
    return chatErrorResult(provider, operation);
  }
}
